import { Component, computed, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import {
  deliveryZonesFor,
  highestZoneFee,
  type ZoneFees,
  zoneControlName,
} from "@components/us-delivery-zones";
import { IApiResponse } from "@models/api-response.model";
import { Country } from "@models/country.model";
import { AppStateService, RequestService } from "@services/index";

interface IDeliveryInfo {
  iso2: string;
  deliveryText: string;
  standardDeliveryFee: number;
  minSubtotalEnabled?: boolean;
  minSubtotalValue?: number;
  /** US only: per-zone fees, {"1": n, ..., "5": n}. */
  deliveryZoneFees?: ZoneFees | null;
  /** Shown to a shopper collecting in person; empty means none is offered. */
  walkInText?: string;
  /** Set by FishyHub: "merchant" (Merchant Self Delivery) or "fishyhub". */
  deliveryMode?: string;
}

interface DeliveryRow {
  iso2: string;
  formGroup: FormGroup;
}

/** A non-negative amount with up to two decimal places. */
const AMOUNT = /^\d+(\.\d{1,2})?$/;

/** What a market's fee starts at before anything is saved (as the database gives new merchants). */
const DEFAULT_FEE: Record<string, number> = { SG: 10, US: 50 };

const DEFAULT_DELIVERY_TEXT =
  "Delivery takes 3 to 7 working days. Delivery fees will be shown upon checkout.";

/**
 * The merchant's own delivery settings, one row per destination country — the same
 * fields the admin portal edits under Vendor > Delivery Information. The API reads
 * and writes only the logged-in merchant's rows.
 *
 * These are the defaults a newly added product country takes; US is priced per
 * zone by the customer's shipping-address state.
 */
@Component({
  selector: "fh-delivery-info",
  templateUrl: "./delivery-info.component.html",
  styleUrls: ["./delivery-info.component.scss"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatTableModule,
  ],
})
export class DeliveryInfoComponent implements OnInit {
  rows = signal<DeliveryRow[]>([]);
  destinationCountries = computed(() => this.appStateService.destinationCountries());
  readonly displayedColumns = [
    "iso2",
    "deliveryText",
    "standardDeliveryFee",
    "walkInText",
    "minSubtotal",
    "actions",
  ];
  readonly deliveryZonesFor = deliveryZonesFor;
  readonly zoneControlName = zoneControlName;

  error = signal("");
  success = signal("");
  savingIso2 = signal<string | null>(null);

  /**
   * Who delivers in each market, set by FishyHub. The fees are the merchant's to
   * change only with Merchant Self Delivery; with FishyHub Delivery (also when
   * nothing is saved yet) they are read-only, and the server keeps them as they
   * are.
   */
  readonly deliveryModes = signal<Record<string, string>>({});

  feeLocked(iso2: string): boolean {
    return this.deliveryModes()[iso2] !== "merchant";
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly requestService: RequestService,
    private readonly appStateService: AppStateService
  ) {}

  ngOnInit() {
    // Countries are normally loaded by the product form; this page can be the first
    // one a merchant opens, so load them here when they aren't there yet.
    if (this.appStateService.allCountries().length > 0) {
      this.buildRowsAndLoad();
      return;
    }
    this.requestService.get<IApiResponse<Country[]>>(`/api/v2/country`).subscribe((res) => {
      if (res.status === true) this.appStateService.allCountries.set(res.data);
      this.buildRowsAndLoad();
    });
  }

  /**
   * Only the markets FishyHub has activated for this store (admin portal >
   * vendor > Delivery Information, the ISO2 checkboxes); every market when no
   * restriction is set, or the list can't be loaded.
   */
  private buildRowsAndLoad() {
    this.requestService
      .get<IApiResponse<{ allowedMarkets: string[] | null }>>(`/api/v2/allowed-markets`)
      .subscribe({
        next: (res) => this.buildRows(res.status === true ? res.data?.allowedMarkets : null),
        error: () => this.buildRows(null),
      });
  }

  private buildRows(allowedMarkets: string[] | null | undefined) {
    const allowed = allowedMarkets ? new Set(allowedMarkets.map((m) => m.toUpperCase())) : null;
    this.rows.set(
      this.destinationCountries()
        .filter((c) => !allowed || allowed.has(c.iso2.toUpperCase()))
        .map((c) => ({
          iso2: c.iso2,
          formGroup: this.createFormGroup(c.iso2),
        }))
    );
    this.loadSaved();
  }

  private createFormGroup(iso2: string): FormGroup {
    const amount = [Validators.required, Validators.min(0), Validators.pattern(AMOUNT)];
    // A plain FormGroup: the US row gains zone controls below, which a strictly
    // typed group would reject by name.
    const group: FormGroup = this.fb.group({
      deliveryText: [DEFAULT_DELIVERY_TEXT, Validators.required],
      // Until saved: the fee a new row starts with (SGD 10, USD 50 per zone).
      standardDeliveryFee: [DEFAULT_FEE[iso2] ?? 0, amount],
      minSubtotalEnabled: [false],
      minSubtotalValue: [0],
      // Optional and free-form: collection is the merchant's own arrangement,
      // and leaving it blank is how they say they do not offer it.
      walkInText: [""],
    });

    // The minimum subtotal is required, and must be positive, only while enabled.
    const minValue = group.get("minSubtotalValue")!;
    group.get("minSubtotalEnabled")?.valueChanges.subscribe((enabled) => {
      minValue.setValidators(
        enabled
          ? [Validators.required, Validators.min(0.01), Validators.pattern(AMOUNT)]
          : [Validators.min(0), Validators.pattern(AMOUNT)]
      );
      minValue.updateValueAndValidity();
    });

    // A zoned market (US: five, MY: two) replaces the single fee with zone fees. The
    // single fee stays in the form, hidden and held at the highest zone.
    const zones = deliveryZonesFor(iso2);
    if (zones.length > 0) {
      for (const z of zones) {
        group.addControl(zoneControlName(z.zone), this.fb.control(DEFAULT_FEE[iso2] ?? 0, amount));
      }
      const syncSingleFee = () =>
        group
          .get("standardDeliveryFee")
          ?.setValue(highestZoneFee(this.zoneFeesOf(iso2, group)), { emitEvent: false });
      for (const z of zones) {
        group.get(zoneControlName(z.zone))?.valueChanges.subscribe(syncSingleFee);
      }
    }
    return group;
  }

  private zoneFeesOf(iso2: string, group: FormGroup): ZoneFees {
    const fees: ZoneFees = {};
    for (const z of deliveryZonesFor(iso2)) {
      fees[String(z.zone) as keyof ZoneFees] =
        Number(group.get(zoneControlName(z.zone))?.value) || 0;
    }
    return fees;
  }

  private loadSaved() {
    this.requestService
      .get<IApiResponse<IDeliveryInfo[]>>(`/api/v2/delivery-info`)
      .subscribe((res) => {
        if (res.status !== true || !Array.isArray(res.data)) return;
        const saved = res.data;
        this.deliveryModes.set(
          Object.fromEntries(saved.map((r) => [r.iso2, r.deliveryMode ?? "fishyhub"]))
        );
        const has = new Set(saved.map((r) => r.iso2));
        // Countries already configured first, then the rest alphabetically.
        this.rows.update((rows) =>
          [...rows].sort((a, b) =>
            has.has(a.iso2) === has.has(b.iso2)
              ? a.iso2.localeCompare(b.iso2)
              : has.has(a.iso2)
                ? -1
                : 1
          )
        );
        for (const info of saved) {
          const row = this.rows().find((r) => r.iso2 === info.iso2);
          if (!row) continue;
          row.formGroup.patchValue({
            deliveryText: info.deliveryText || DEFAULT_DELIVERY_TEXT,
            standardDeliveryFee: info.standardDeliveryFee ?? 0,
            minSubtotalEnabled: info.minSubtotalEnabled ?? false,
            minSubtotalValue: info.minSubtotalValue || 0,
            walkInText: info.walkInText ?? "",
          });
          // Zones (US, MY): saved values, or the current single fee for a merchant who
          // has none yet — so saving without editing keeps today's pricing.
          for (const z of deliveryZonesFor(info.iso2)) {
            const v = info.deliveryZoneFees?.[String(z.zone) as keyof ZoneFees];
            row.formGroup
              .get(zoneControlName(z.zone))
              ?.setValue(v ?? info.standardDeliveryFee ?? 0);
          }
        }
      });
  }

  save(iso2: string) {
    const row = this.rows().find((r) => r.iso2 === iso2);
    if (!row || row.formGroup.invalid || this.savingIso2()) return;
    if (!window.confirm(`Save delivery information for ${iso2}?`)) return;

    const payload: IDeliveryInfo = {
      iso2,
      ...row.formGroup.value,
      ...(deliveryZonesFor(iso2).length > 0
        ? { deliveryZoneFees: this.zoneFeesOf(iso2, row.formGroup) }
        : {}),
    };
    this.savingIso2.set(iso2);
    this.requestService.post<IApiResponse>(`/api/v2/delivery-info`, payload).subscribe({
      next: (res) => {
        this.savingIso2.set(null);
        if (res.status) {
          this.flash(this.success, `Delivery information for ${iso2} saved.`);
          this.loadSaved();
        } else {
          this.flash(this.error, res.message || "Failed to save delivery information.");
        }
      },
      error: () => {
        this.savingIso2.set(null);
        this.flash(this.error, "Failed to save delivery information. Please try again.");
      },
    });
  }

  private flash(target: typeof this.success, message: string) {
    target.set(message);
    setTimeout(() => target.set(""), 4000);
  }
}
